import React, { Component } from 'react';

import { get, isEqual, omitBy } from 'lodash-es';
import { stringify, parse } from 'qs';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { Repository } from 'src/components/types';
import { BRANCH_KIND } from 'src/constants/branching-model';
import { SCROLL_TO_TOP_DURATION } from 'src/constants/permalink-scroll';
import {
  clearSelectedBranches,
  loadBranches,
  loadMainBranch,
} from 'src/redux/branches';
import scrollTo from 'src/redux/global/actions/scroll-to';
import { RouteComponentProps } from 'src/router/types';
import withLocation from 'src/router/utils/with-location';
import { LoadingPage } from 'src/sections/global/components/loading-page';
import {
  getCurrentRepository,
  getCurrentRepositoryMainBranchName,
} from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import escapeBbqlString from 'src/utils/escape-bbql-string';

import BranchesPage from './components/branches-page';
import { Filter, Params, Sort, Status } from './types';

const PAGE_LEN = 25;
export const STALE_CRITERIA_DAYS = 28;

type Props = RouteComponentProps<{
  repositoryOwner: string;
  repositorySlug: string;
}> & {
  clearSelectedBranches: () => void;
  isEmpty: boolean;
  repository: Repository;
  loadMainBranch: typeof loadMainBranch;
  loadBranches: typeof loadBranches;
  mainBranchName?: string;
  scrollTo: typeof scrollTo;
};

export class BranchListRoute extends Component<Props> {
  componentDidMount() {
    const { repository, isEmpty } = this.props;

    if (repository && !isEmpty) {
      this.loadPage();
    }
  }

  shouldComponentUpdate(prevProps: Props) {
    return (
      this.props.location.search !== prevProps.location.search ||
      this.repositoryDidUpdate(prevProps)
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.repositoryDidUpdate(prevProps)) {
      if (!this.props.isEmpty) {
        this.loadPage();
      }

      return;
    }

    if (this.props.location.search !== prevProps.location.search) {
      this.loadBranches();
    }
  }

  repositoryDidUpdate(prevProps: Props) {
    return (
      get(prevProps.repository, 'uuid', '') !==
      get(this.props.repository, 'uuid', '')
    );
  }

  loadPage = () => {
    this.loadMainBranch();
    this.loadBranches();
  };

  fallbackParams: Params = {
    sort: '-target.date',
    page: 1,
    status: Status.Active,
    search: '',
    branchtype: [],
  };

  loadMainBranch = () => {
    const { repositoryOwner, repositorySlug } = this.props.match.params;
    const { mainBranchName } = this.props;
    if (!repositoryOwner || !repositorySlug || !mainBranchName) {
      return;
    }

    const options = {
      repositoryOwner,
      repositorySlug,
      name: mainBranchName,
    };

    this.props.loadMainBranch(options);
  };

  loadBranches = () => {
    const { repositoryOwner, repositorySlug } = this.props.match.params;
    if (!repositoryOwner || !repositorySlug) {
      return;
    }

    let params = this.getParams();
    params = omitBy(params, (v, k) =>
      // @ts-ignore TODO: fix noImplicitAny error here
      isEqual(this.fallbackParams[k], v)
    ) as Params;

    const { status, search, branchtype, sort, page } = params;
    const options = {
      repositoryOwner,
      repositorySlug,
      sort,
      page,
      pagelen: PAGE_LEN,
      bbql: this.buildBBQL({
        status: status || Status.Active,
        search,
        branchtype,
      }),
    };

    this.props.loadBranches(options);
  };

  getParams = (): Params => {
    return {
      ...this.fallbackParams,
      ...this.parseQuery(),
    };
  };

  parseQuery = (): Partial<Params> => {
    const params = parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });

    const branchtype =
      params.branchtype && !Array.isArray(params.branchtype)
        ? ([params.branchtype] as BRANCH_KIND[])
        : (params.branchtype as BRANCH_KIND[] | undefined);

    return {
      ...params,
      branchtype,
      ...(params.page && { page: parseInt(params.page as string, 10) || 1 }),
    };
  };

  getStaleBranchThreshold = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - STALE_CRITERIA_DAYS);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // months are 0 based
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  buildBBQL = ({ status, search, branchtype }: Filter) => {
    const bbql: string[] = [];

    const { mainBranchName } = this.props;
    if (mainBranchName) {
      // Main branch is always displayed on top of the list irrespective
      // of the sort/filter condition. So it is excluded and fetched it separately.
      bbql.push(`name != "${escapeBbqlString(mainBranchName)}"`);
    }

    if (status === Status.Active) {
      bbql.push('(ahead > 0 OR ahead = null)');
    } else if (status === Status.Merged) {
      bbql.push('(ahead = 0 OR ahead = null)');
    } else if (status === 'stale') {
      bbql.push(
        `(ahead = 0 OR ahead = null) AND target.date<${this.getStaleBranchThreshold()}`
      );
    }

    if (search) {
      bbql.push(`name ~ "${escapeBbqlString(search)}"`);
    }

    if (branchtype && branchtype.length) {
      const typeBbql = branchtype.map(t => `branchtype = "${t}"`).join(' OR ');
      bbql.push(`(${typeBbql})`);
    }

    return bbql.join(' AND ');
  };

  updateUrl = (params: Partial<Params>) => {
    const {
      location: { pathname },
    } = this.props;

    const nextParams: Partial<Params> = {
      ...this.parseQuery(),
      ...params,
      ...{ page: params.page || 1 },
    };

    const urlParams = omitBy(nextParams, (v, k) =>
      // @ts-ignore TODO: fix noImplicitAny error here
      isEqual(this.fallbackParams[k], v)
    );

    // `indices: false` is for nicer URLs for multi-valued keys.
    // Otherwise multiple `branchtype` params would look like this:
    // `?branchtype[0]=feature&branchtype[1]=bugfix`
    // With `indices: false` they look like this:
    // `?branchtype=feature&branchtype=bugfix`
    const query = stringify(urlParams, { skipNulls: true, indices: false });
    return `${pathname}?${query}`;
  };

  handleFilterChange = (filter: Partial<Filter>) => {
    this.props.history.push(this.updateUrl(filter));
    this.props.clearSelectedBranches();
  };

  handlePageChange = () => {
    this.props.scrollTo({
      targetId: 'root',
      duration: SCROLL_TO_TOP_DURATION,
    });
  };

  handleSortChange = (sort: Sort) => {
    this.props.history.push(this.updateUrl({ sort }));
    this.props.clearSelectedBranches();
  };

  render() {
    const { repository } = this.props;

    if (!repository) {
      return <LoadingPage />;
    }

    const params = this.getParams();

    const { status, search, branchtype, sort, page } = params;
    const filter = { status, search, branchtype };
    const getPaginationUrl = (pageNum: number) =>
      this.updateUrl({ page: pageNum });

    return (
      <BranchesPage
        sort={sort}
        page={page}
        filter={filter}
        repositoryFullSlug={repository.full_name}
        getPaginationUrl={getPaginationUrl}
        onLoadPage={this.loadPage}
        onPageChange={this.handlePageChange}
        onSortChange={this.handleSortChange}
        onFilterChange={this.handleFilterChange}
        isEmpty={this.props.isEmpty}
        isStale
      />
    );
  }
}

const mapStateToProps = (state: BucketState) => ({
  isEmpty: !getCurrentRepositoryMainBranchName(state),
  repository: getCurrentRepository(state),
  mainBranchName: getCurrentRepositoryMainBranchName(state),
});

const mapDispatchToProps = {
  clearSelectedBranches,
  loadMainBranch,
  loadBranches,
  scrollTo,
};

export default compose(
  withLocation,
  connect(mapStateToProps, mapDispatchToProps)
)(BranchListRoute);
